import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  EnvelopeSimple, Eye, EyeSlash, FloppyDisk, PaperPlaneTilt, CheckCircle, XCircle,
  CaretDown, CaretRight, Bell,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { EmailNotificationsConfig } from './EmailNotificationsConfig';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CollapsibleCard = ({ icon: Icon, title, description, open, onToggle, badge, testid, children }) => (
  <div className="bg-white border border-[#EDE6D8] rounded-lg overflow-hidden" data-testid={testid}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-[#FCFAF6]/50 transition-colors"
      data-testid={`${testid}-toggle`}
    >
      <div className="flex items-start gap-3 min-w-0">
        {Icon && <Icon size={22} className="text-[#0A2540] mt-0.5 flex-shrink-0" />}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {title}
            </h2>
            {badge}
          </div>
          {description && (
            <p className="text-sm text-[#78716C] mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {description}
            </p>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 text-[#78716C]">
        {open ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
      </div>
    </button>
    {open && (
      <div className="px-6 pb-6 border-t border-[#EDE6D8] pt-6">
        {children}
      </div>
    )}
  </div>
);

export const EmailsConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [smtpOpen, setSmtpOpen] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);

  const [form, setForm] = useState({
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
    from_email: '', from_name: '', use_tls: true,
  });

  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/email/config`, { withCredentials: true, headers: authHeaders() });
      if (data) {
        setConfig(data);
        setForm({
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || '',
          smtp_password: '',
          from_email: data.from_email || '',
          from_name: data.from_name || '',
          use_tls: data.use_tls !== false,
        });
      }
    } catch (e) {
      if (e?.response?.status !== 404) console.error('email config fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    if (!form.smtp_host.trim()) { toast.error('SMTP Host is required'); return; }
    if (!form.smtp_port) { toast.error('SMTP Port is required'); return; }
    if (!form.smtp_username.trim()) { toast.error('SMTP Username is required'); return; }
    if (!config && !form.smtp_password.trim()) { toast.error('SMTP Password is required'); return; }
    if (!form.from_email.trim()) { toast.error('From Email is required'); return; }
    setSaving(true);
    try {
      const payload = {
        smtp_host: form.smtp_host.trim(),
        smtp_port: Number(form.smtp_port),
        smtp_username: form.smtp_username.trim(),
        from_email: form.from_email.trim(),
        from_name: form.from_name.trim(),
        use_tls: form.use_tls,
      };
      if (form.smtp_password.trim()) payload.smtp_password = form.smtp_password.trim();
      const { data } = await axios.post(`${API_URL}/api/email/config`, payload, { withCredentials: true, headers: authHeaders() });
      setConfig(data);
      setForm(f => ({ ...f, smtp_password: '' }));
      toast.success('SMTP configuration saved');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save configuration'));
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = () => {
    setTestEmail('');
    setTestDialogOpen(true);
  };

  const handleTest = async () => {
    if (!testEmail.trim()) { toast.error('Enter a recipient email address'); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/email/test`,
        { to_email: testEmail.trim() },
        { withCredentials: true, headers: authHeaders() },
      );
      toast.success(data?.message || 'Test email sent');
      setTestDialogOpen(false);
      fetchConfig();
    } catch (e) {
      toast.error(formatApiError(e, 'Test email failed'));
      fetchConfig();
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#EDE6D8] rounded-lg p-12 text-center text-sm text-[#78716C]" data-testid="emails-config-loading">
        Loading email configuration…
      </div>
    );
  }

  const testStatusChip = config?.last_test_at && (
    <span className="inline-flex items-center gap-1.5 text-xs" data-testid="last-test-status">
      {config.last_test_status === 'success' ? (
        <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
      ) : (
        <XCircle size={14} weight="fill" className="text-[#EF4444]" />
      )}
      <span className={config.last_test_status === 'success' ? 'text-[#10B981]' : 'text-[#EF4444]'}>
        Last test {config.last_test_status === 'success' ? 'succeeded' : 'failed'}
      </span>
    </span>
  );

  return (
    <div className="space-y-4" data-testid="emails-config">
      {/* SMTP Server Settings — collapsible */}
      <CollapsibleCard
        icon={EnvelopeSimple}
        title="SMTP Server Settings"
        description="Configure your email server for sending notifications"
        open={smtpOpen}
        onToggle={() => setSmtpOpen(v => !v)}
        badge={testStatusChip}
        testid="smtp-settings-card"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>SMTP Host <span className="text-[#EF4444]">*</span></Label>
            <Input
              value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              placeholder="mail.ecowaterinternational.com"
              className="border-[#EDE6D8] rounded-lg"
              data-testid="smtp-host-input"
            />
          </div>
          <div className="space-y-2">
            <Label>SMTP Port <span className="text-[#EF4444]">*</span></Label>
            <Input
              type="number"
              min="1"
              max="65535"
              value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
              placeholder="587"
              className="border-[#EDE6D8] rounded-lg"
              data-testid="smtp-port-input"
            />
          </div>
          <div className="space-y-2">
            <Label>SMTP Username <span className="text-[#EF4444]">*</span></Label>
            <Input
              value={form.smtp_username}
              onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
              placeholder="console@ecowaterinternational.com"
              className="border-[#EDE6D8] rounded-lg"
              data-testid="smtp-username-input"
            />
          </div>
          <div className="space-y-2">
            <Label>SMTP Password {config ? '' : <span className="text-[#EF4444]">*</span>}</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.smtp_password}
                onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                placeholder="Enter password"
                className="border-[#EDE6D8] rounded-lg pr-10"
                data-testid="smtp-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
                data-testid="smtp-password-toggle"
              >
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {config && (
              <p className="text-xs text-[#78716C]">Leave blank to keep existing password</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>From Email <span className="text-[#EF4444]">*</span></Label>
            <Input
              type="email"
              value={form.from_email}
              onChange={(e) => setForm({ ...form, from_email: e.target.value })}
              placeholder="console@ecowaterinternational.com"
              className="border-[#EDE6D8] rounded-lg"
              data-testid="smtp-from-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label>From Name</Label>
            <Input
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              placeholder="Ecowater Console"
              className="border-[#EDE6D8] rounded-lg"
              data-testid="smtp-from-name-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-[#FCFAF6] rounded-lg mt-4">
          <Switch
            checked={form.use_tls}
            onCheckedChange={(checked) => setForm({ ...form, use_tls: checked })}
            data-testid="smtp-tls-switch"
          />
          <div>
            <Label className="cursor-pointer font-medium">Use TLS Encryption</Label>
            <p className="text-xs text-[#78716C] mt-0.5">Recommended for secure email delivery</p>
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-3 pt-4 mt-4 border-t border-[#EDE6D8]">
          <div className="text-xs text-[#78716C]" data-testid="last-test-detail">
            {config?.last_test_at ? (
              <>Last test {config.last_test_status === 'success' ? 'succeeded' : 'failed'} · {new Date(config.last_test_at).toLocaleString()}
                {config.last_test_status !== 'success' && config.last_test_error && (
                  <span className="ml-1" title={config.last_test_error}>— {config.last_test_error}</span>
                )}
              </>
            ) : 'No test emails sent yet.'}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openTestDialog}
              disabled={!config}
              variant="outline"
              className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/10 rounded-lg"
              data-testid="open-test-email-btn"
              title={!config ? 'Save the configuration first' : ''}
            >
              <PaperPlaneTilt size={16} className="mr-2" />
              Send Test Email
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="save-email-config-btn"
            >
              <FloppyDisk size={16} className="mr-2" />
              {saving ? 'Saving…' : 'Save Configuration'}
            </Button>
          </div>
        </div>
      </CollapsibleCard>

      {/* Email Notifications — collapsible */}
      <CollapsibleCard
        icon={Bell}
        title="Email Notifications"
        description="Enable outbound notifications per entity. Select which user groups receive each notification, or send a live test to verify formatting."
        open={notifOpen}
        onToggle={() => setNotifOpen(v => !v)}
        testid="email-notifications-card"
      >
        <EmailNotificationsConfig bare />
      </CollapsibleCard>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent data-testid="smtp-test-dialog">
          <DialogHeader>
            <DialogTitle>Send SMTP test email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#78716C]">
              A short test message will be sent using your saved SMTP configuration.
            </p>
            <div className="space-y-1.5">
              <Label>Recipient email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="border-[#EDE6D8] rounded-lg"
                data-testid="smtp-test-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)} className="rounded-lg" data-testid="smtp-test-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleTest}
              disabled={testing || !config}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="smtp-test-send-btn"
            >
              <PaperPlaneTilt size={16} className={`mr-2 ${testing ? 'animate-pulse' : ''}`} />
              {testing ? 'Sending…' : 'Send test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
